import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import '../styles/LegalPages.css';

const PrivacyPolicy = () => {
  return (
    <div className="legal-page">
      <SEO 
        title="Privacy Policy - VibrantCast"
        description="VibrantCast Privacy Policy - Learn how we collect, use, and protect your information."
      />
      
      <nav className="legal-nav">
        <div className="legal-nav-container">
          <Link to="/" className="legal-logo">
            <img 
              src="/images/inline-logo-white-1.png" 
              alt="VibrantCast" 
              className="legal-logo-img"
            />
          </Link>
          <Link to="/login" className="legal-nav-btn">Sign In</Link>
        </div>
      </nav>

      <div className="legal-content">
        <div className="legal-container">
          <h1>VibrantCast Privacy Policy</h1>
          
          <p className="legal-intro">
            VibrantCast respects the privacy of our users and has developed this Privacy Policy to demonstrate its commitment to protecting your privacy. This Privacy Policy is intended to describe for you, as an individual who is a user of VibrantCast (and all websites and URL's controlled or operated by VibrantCast which link to this policy, unless otherwise specified) or our services, or otherwise provide us with information through various means the information we collect, how that information may be used, with whom it may be shared, and your choices about such uses and disclosures.
          </p>
          <p className="legal-intro">
            We encourage you to read this Privacy Policy carefully when using our website or services or transacting business with us. By using our website, you are accepting the practices described in this Privacy Policy. If you have any questions about our privacy practices, please refer to the end of this Privacy Policy for information on how to contact us.
          </p>

          <section className="legal-section">
            <h2>1. Information We Collect</h2>
            
            <h3>a. Information You Provide to Us</h3>
            <ul>
              <li><strong>Account Information:</strong> When you sign up, we collect your name, email address, username, and password.</li>
              <li><strong>Profile Information:</strong> You may choose to share additional details like a bio, profile photo, and location.</li>
              <li><strong>Uploaded Content:</strong> Any artwork, images, videos, audio stories, or descriptions you upload to the Platform.</li>
              <li><strong>Messages and Inquiries:</strong> Content of any messages or inquiries sent through the Platform, including to and from artists or collectors.</li>
            </ul>

            <h3>b. Information We Collect Automatically</h3>
            <ul>
              <li><strong>Device and Usage Information:</strong> IP address, device type, browser, and interactions with the Platform (e.g., pages viewed, features used).</li>
              <li><strong>Cookies and Tracking Technologies:</strong> We use cookies and similar technologies to enhance your experience and understand how the Platform is used.</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>2. How We Use Your Information</h2>
            <p>We use your information to:</p>
            <ul>
              <li>Provide and improve the Platform and our services</li>
              <li>Enable features like user profiles, artwork uploads, and messaging</li>
              <li>Display your content to other users, based on your settings</li>
              <li>Respond to inquiries and provide customer support</li>
              <li>Send service-related communications (e.g., updates or confirmations)</li>
              <li>Promote VibrantCast events, features, and content (with attribution to creators when applicable)</li>
              <li>Ensure security and enforce our Terms of Use</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>3. How We Share Your Information</h2>
            <p>We may share your information in the following ways:</p>
            <ul>
              <li>With other users, based on your activity and what you choose to post.</li>
              <li>With service providers who help us operate the Platform, such as hosting, analytics, or customer service vendors.</li>
              <li>With third-party payment processors, if and when payments are integrated (e.g., Stripe), subject to their terms and privacy policies.</li>
              <li>With event partners or collaborators, if you participate in a VibrantCast-powered event.</li>
              <li>To comply with legal obligations or respond to lawful requests from public authorities.</li>
            </ul>
            <p><strong>We do not sell your personal information.</strong></p>
          </section>

          <section className="legal-section">
            <h2>4. Your Choices</h2>
            <p>You can:</p>
            <ul>
              <li>Update or correct your profile information via your account settings.</li>
              <li>Delete your account at any time.</li>
              <li>Manage cookie preferences through your browser.</li>
              <li>Opt out of non-essential communications via unsubscribe links or by contacting us.</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>5. Data Retention</h2>
            <p>
              We retain your information for as long as needed to provide services and fulfill the purposes described in this Policy. You may request deletion of your account and data at any time, and we will delete it unless we have a legitimate legal or operational reason to retain it.
            </p>
          </section>

          <section className="legal-section">
            <h2>6. Security</h2>
            <p>
              We take the security of your personal information seriously. VibrantCast uses reasonable administrative, technical, and physical safeguards to protect your data from unauthorized access, loss, misuse, or alteration.
            </p>
            <p>
              However, no method of transmission over the internet or method of electronic storage is 100% secure. We encourage you to take steps to protect your own information as well, such as using strong passwords and logging out of your account when using shared devices.
            </p>
          </section>

          <section className="legal-section">
            <h2>7. Children's Privacy</h2>
            <p>
              VibrantCast is not directed toward children under 13. We do not knowingly collect personal information from children under 13. If we learn that a user under 13 has registered, we will take reasonable steps to delete the associated information.
            </p>
          </section>

          <section className="legal-section">
            <h2>8. International Users</h2>
            <p>
              If you access the Platform from outside the United States, be aware that your information will be processed and stored in the U.S., and subject to U.S. laws.
            </p>
          </section>

          <section className="legal-section">
            <h2>9. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of material changes by email or within the app. Continued use of the Platform after changes take effect indicates your acceptance of the updated policy.
            </p>
          </section>

          <section className="legal-section">
            <h2>10. Contact Us</h2>
            <p>
              If you have questions or concerns about this Privacy Policy or our practices, contact us at:
            </p>
            <p className="legal-contact">
              📧 <a href="mailto:support@vibrantcast.com">support@vibrantcast.com</a>
            </p>
          </section>
        </div>
      </div>

      <footer className="legal-footer">
        <div className="legal-footer-container">
          <p>© {new Date().getFullYear()} VibrantCast, LLC. All rights reserved.</p>
          <div className="legal-footer-links">
            <Link to="/terms">Terms of Service</Link>
            <Link to="/privacy">Privacy Policy</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
